import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, Download } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { downloadFile } from '@/lib/download';
import { reopenCookieBanner } from '@/components/CookieConsent';
import { LOGO_WHITE } from '@/lib/brandImages';

const BROCHURE_URL = 'https://storage.googleapis.com/daley-inventories-assets/brochure.pdf';
const SAMPLE_REPORT_URL = 'https://storage.googleapis.com/daley-inventories-assets/sample-report.pdf';

export default function Footer() {
  const { toast } = useToast();

  const handleDownload = async (url, filename, label) => {
    try {
      await downloadFile(url, filename);
    } catch {
      toast({ title: 'Download Error', description: `Could not download ${label}. Please try again.`, variant: 'destructive' });
    }
  };

  return (
    <footer className="bg-navy text-white">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <img src={LOGO_WHITE.src} width={LOGO_WHITE.width} height={LOGO_WHITE.height} loading="lazy" alt="Daley Inventories Ltd" className="h-14 w-auto mb-4" />
            <p className="text-white/60 text-sm leading-relaxed max-w-md">
              10 years of trusted property inventory, safety certification, and cleaning services across London and Manchester.
            </p>
          </div>
          <div>
            <p className="caps-label text-orange mb-4">Company</p>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/about" className="hover:text-orange transition-colors">About Us</Link></li>
              <li><Link to="/testimonials" className="hover:text-orange transition-colors">Testimonials</Link></li>
              <li><Link to="/service-areas" className="hover:text-orange transition-colors">Service Areas</Link></li>
              <li><Link to="/blog" className="hover:text-orange transition-colors">Blog</Link></li>
              <li><Link to="/sample-reports" className="hover:text-orange transition-colors">Sample Reports</Link></li>
              <li><Link to="/careers" className="hover:text-orange transition-colors">Careers</Link></li>
            </ul>
          </div>
          <div>
            <p className="caps-label text-orange mb-4">Services</p>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/services" className="hover:text-orange transition-colors">Inventory Reports</Link></li>
              <li><Link to="/property-inventory-london" className="hover:text-orange transition-colors">London inventories</Link></li>
              <li><Link to="/property-inventory-manchester" className="hover:text-orange transition-colors">Manchester inventories</Link></li>
              <li><Link to="/letting-agent-inventories" className="hover:text-orange transition-colors">For letting agents</Link></li>
              <li><Link to="/services" className="hover:text-orange transition-colors">Safety Certificates</Link></li>
              <li><Link to="/services" className="hover:text-orange transition-colors">End-of-Tenancy Cleaning</Link></li>
              <li><Link to="/pricing" className="hover:text-orange transition-colors">Pricing</Link></li>
              <li><Link to="/faq" className="hover:text-orange transition-colors">FAQ</Link></li>
            </ul>
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={() => handleDownload(BROCHURE_URL, 'DaleyInventories-Brochure-2026.pdf', 'the brochure')}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-orange/50 text-orange text-xs font-semibold uppercase tracking-widest hover:bg-orange hover:text-navy transition-all duration-200"
              >
                <Download size={13} />
                Download Brochure
              </button>
              <button
                onClick={() => handleDownload(SAMPLE_REPORT_URL, 'DaleyInventories-SampleReport-2026.pdf', 'the sample report')}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-orange/50 text-orange text-xs font-semibold uppercase tracking-widest hover:bg-orange hover:text-navy transition-all duration-200"
              >
                <Download size={13} />
                Sample Report
              </button>
            </div>
          </div>
          <div>
            <p className="caps-label text-orange mb-4">Contact</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2"><Phone size={14} /> <a href="tel:02089355492" className="hover:text-orange transition-colors">0208 935 5492</a></li>
              <li className="flex items-center gap-2"><Mail size={14} /> <a href="mailto:info@daleyinventories.com" className="hover:text-orange transition-colors">info@daleyinventories.com</a></li>
              <li className="flex items-center gap-2"><Clock size={14} /> Mon–Sun, 9am–6pm</li>
              <li className="flex items-center gap-2"><MapPin size={14} /> <Link to="/contact" className="hover:text-orange transition-colors">London · Manchester</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 mt-16 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/70">© 2026 Daley Inventories Ltd. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/70">
            <Link to="/privacy-policy" className="hover:text-orange transition-colors">Privacy Policy</Link>
            <Link to="/gdpr" className="hover:text-orange transition-colors">GDPR &amp; Your Rights</Link>
            <button onClick={reopenCookieBanner} className="hover:text-orange transition-colors">Cookie Preferences</button>
          </div>
          <p className="text-xs text-white/70">AIIC · NICEIC · Gas Safe Registered · EPC Registered</p>
        </div>
      </div>
    </footer>
  );
}